import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import cx from 'classnames';

import { useId, useInputBooleanValue } from 'utilsTS/hooks';
import { mergeRefs } from 'utilsTS/react';

import { LabeledInput } from './components/LabeledInput';
import { type InputProps, inputPropTypes } from './inputPropTypes';

export interface CheckboxInputProps extends Omit<InputProps, 'onBeforeChange' | 'onChange'> {
    innerRef?: React.Ref<HTMLInputElement>;
    label?: React.ReactNode;
    indeterminate?: boolean;
    onBeforeChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean,
    ) => boolean | void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

export const CheckboxInput: React.FC<CheckboxInputProps> = ({
    innerRef,
    id: defaultId = '',
    label = '',
    value,
    checked: controlledChecked = false,
    defaultChecked,
    readOnly,
    indeterminate = false,
    children,
    className,
    onClick = noop,
    onBeforeChange = () => true,
    onChange = noop,
    ...rest
}) => {
    const ref = React.useRef<HTMLInputElement>();
    const id = useId('checkbox', defaultId);
    const [checked, handleChange] = useInputBooleanValue(defaultChecked, controlledChecked, {
        readOnly,
        onBeforeChange,
        onChange,
    });

    useEffect(() => {
        if (ref.current) ref.current.indeterminate = !!indeterminate;
    }, [indeterminate]);

    return (
        <LabeledInput
            htmlFor={id}
            label={label}
            className={cx('custom-checkbox', className)}
            onClick={onClick}
        >
            <input
                {...rest}
                ref={mergeRefs(ref, innerRef)}
                id={id}
                type="checkbox"
                value={value}
                checked={checked}
                className="custom-control-input"
                onChange={handleChange}
                onClick={onClick}
            />
            {children}
        </LabeledInput>
    );
};

CheckboxInput.displayName = 'CheckboxInput';
CheckboxInput.propTypes = {
    ...inputPropTypes,
    id: PropTypes.string,
    label: PropTypes.node,
    checked: PropTypes.bool,
    indeterminate: PropTypes.bool,
    children: PropTypes.node,
    onClick: PropTypes.func,
};
